import React from 'react';
import { getApiClient } from './apiClient';
import { useTenant } from 'contexts/TenantContext';
import logger from './logger';

/**
 * Get the main tenant for a user
 * @param {string} userId - User ID (optional, will use current user if not provided)
 * @param {boolean} isPerPageCheck - Whether this is a per-page check (for request naming)
 * @returns {Promise<{success: boolean, tenant: Object|null, error: string|null}>}
 */
export async function getMainTenant(userId = null, isPerPageCheck = false) {
  try {
    const apiClient = getApiClient();
    
    // Get user profile which includes tenant information
    const userProfile = await apiClient.getUserProfile();
    
    if (!userProfile.success || !userProfile.data) {
      return {
        success: false,
        tenant: null,
        error: 'Could not fetch user profile'
      };
    }
    
    const user = userProfile.data;
    
    // Get tenant ID from user's preferred tenant or first tenant
    let tenantId = user.preferred_tenant_id || user.tenant_ids?.[0];
    
    if (!tenantId) {
      return {
        success: false,
        tenant: null,
        error: 'User has no associated tenants'
      };
    }
    
    // Fetch tenant details
    let requestName = null;
    if (isPerPageCheck) {
      const { generateRequestName } = await import('./requestNaming');
      requestName = generateRequestName('pp', 'get', 'checkpartner');
    }
    const tenant = await apiClient.getTenant(tenantId, requestName);
    
    if (!tenant.success) {
      return {
        success: false,
        tenant: null,
        error: tenant.error || 'Could not fetch tenant details'
      };
    }
    
    return {
      success: true,
      tenant: tenant.data || tenant,
      error: null
    };
  } catch (error) {
    logger.error('TENANT-UTILS', 'Error getting main tenant', { error: error.message });
    return {
      success: false,
      tenant: null,
      error: error.message || 'Unknown error'
    };
  }
}

/**
 * Check if a tenant is a partner tenant
 * @param {Object} tenant - Tenant object
 * @returns {boolean}
 */
export function isPartnerTenant(tenant) {
  if (!tenant) return false;
  
  // Check multiple possible field names for partner status
  return (
    tenant.tenant_type === 'partner' ||
    tenant.tenant_category === 'partner' ||
    tenant.is_partner === true ||
    tenant.partner === true ||
    (tenant.metadata && tenant.metadata.is_partner === true)
  );
}

/**
 * Check if a tenant is a managed tenant
 * @param {Object} tenant - Tenant object
 * @returns {boolean}
 */
export function isManagedTenant(tenant) {
  if (!tenant) return false;
  
  return (
    tenant.tenant_type === 'managed' ||
    tenant.managed_by_partner_tenant_id !== undefined ||
    tenant.managed_by_resale_resource_id !== undefined
  );
}

/**
 * Check if the current user's main tenant is a partner
 * @returns {Promise<{isPartner: boolean, tenant: Object|null, error: string|null}>}
 */
export async function checkIsPartner() {
  try {
    // Pass isPerPageCheck=true to mark this as a per-page check
    const tenantResult = await getMainTenant(null, true);
    
    if (!tenantResult.success) {
      return {
        isPartner: false,
        tenant: null,
        error: tenantResult.error
      };
    }
    
    return {
      isPartner: isPartnerTenant(tenantResult.tenant),
      tenant: tenantResult.tenant,
      error: null
    };
  } catch (error) {
    logger.error('TENANT-UTILS', 'Error checking partner status', { error: error.message });
    return {
      isPartner: false,
      tenant: null,
      error: error.message || 'Unknown error'
    };
  }
}

/**
 * React hook to get main tenant and check partner status
 * @returns {Object} - { tenant, isPartner, isLoading, error, refresh }
 */
export function useMainTenant() {
  const { tenant, isLoading: contextLoading } = useTenant();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  // Use tenant from context if available
  const mainTenant = React.useMemo(() => {
    if (tenant) {
      return {
        tenant,
        isPartner: isPartnerTenant(tenant),
        isLoading: contextLoading
      };
    }
    return null;
  }, [tenant, contextLoading]);
  
  const refresh = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getMainTenant();
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  return {
    tenant: mainTenant?.tenant || null,
    isPartner: mainTenant?.isPartner || false,
    isLoading: isLoading || contextLoading,
    error,
    refresh
  };
}

